import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
// @mui
import { alpha, styled } from '@mui/material/styles';
import { Box, Card, CardContent, Chip, CircularProgress, Grid, Stack, Typography } from '@mui/material';
// components
import Iconify from '../../../components/iconify';
import { MotionInView, varFadeInUp } from '../../../components/animate';
// redux
import { useDispatch, useSelector } from '../../../redux/store';
import { getAllCourses } from '../../../redux/slices/course';

// ----------------------------------------------------------------------

const StyledTitle = styled(Typography)({
  overflow: 'hidden',
  minHeight: 48,
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  display: '-webkit-box',
});

const StyledIntro = styled(Typography)({
  overflow: 'hidden',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  display: '-webkit-box',
});

// ----------------------------------------------------------------------

CourseCard.propTypes = {
  course: PropTypes.object.isRequired,
  index: PropTypes.number,
};

function CourseCard({ course, index = 0 }) {
  const { slug, image, name, tag, introduction, videoCount, subjectCount } = course;

  const linkto = `/course/${slug}`;

  // Only render the metrics the payload actually provides, so a partial
  // response never shows "undefined Lessons".
  const stats = [
    { id: 'lessons', icon: 'ic:round-menu-book', text: `${subjectCount} Lessons`, value: subjectCount },
    { id: 'videos', icon: 'ic:round-play-circle', text: `${videoCount} Videos`, value: videoCount },
  ].filter((stat) => Number.isFinite(stat.value));

  return (
    <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ display: 'flex' }}>
      <MotionInView
        variants={varFadeInUp}
        transition={{ delay: Math.min(index, 5) * 0.08 }}
        sx={{ width: '100%' }}
      >
        <Card
          component={RouterLink}
          to={linkto}
          aria-label={`View course: ${name}`}
          sx={{
            position: 'relative',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
            overflow: 'hidden',
            color: 'inherit',
            textDecoration: 'none',
            transition: 'transform 0.4s cubic-bezier(0.43, 0.13, 0.23, 0.96), box-shadow 0.4s ease',
            '&:focus-visible': {
              outline: (theme) => `2px solid ${theme.palette.primary.main}`,
              outlineOffset: 4,
            },
            '@media (hover: hover)': {
              '&:hover': {
                transform: 'translateY(-6px)',
                boxShadow: (theme) => `0 18px 36px ${alpha(theme.palette.grey[900], 0.16)}`,
              },
              '&:hover img': { transform: 'scale(1.06)' },
              '&:hover .course-arrow': { transform: 'translateX(4px)' },
            },
          }}
        >
          <Box
            sx={{
              position: 'relative',
              aspectRatio: '3 / 2',
              overflow: 'hidden',
              bgcolor: 'grey.200',
              '& img': {
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transition: 'transform 0.6s cubic-bezier(0.43, 0.13, 0.23, 0.96)',
              },
            }}
          >
            {image ? (
              <Box component="img" src={image} alt={name} loading="lazy" decoding="async" />
            ) : (
              <Stack sx={{ alignItems: 'center', justifyContent: 'center', position: 'absolute', inset: 0, color: 'text.disabled' }}>
                <Iconify icon="ic:round-menu-book" width={40} />
              </Stack>
            )}

            {tag?.name && (
              <Chip
                label={tag.name}
                size="small"
                sx={{
                  position: 'absolute',
                  top: 12,
                  left: 12,
                  height: 22,
                  maxWidth: '70%',
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                  bgcolor: '#DF3E30',
                  color: 'common.white',
                  '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                }}
              />
            )}
          </Box>

          <CardContent sx={{ p: { xs: 2.25, md: 2.5 }, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            <StyledTitle variant="subtitle1" sx={{ color: 'text.primary', mb: 1 }}>
              {name}
            </StyledTitle>

            {introduction && (
              <StyledIntro variant="body2" sx={{ color: 'text.secondary', mb: 2, flexGrow: 1 }}>
                {introduction}
              </StyledIntro>
            )}

            {stats.length > 0 && (
              <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
                {stats.map((stat) => (
                  <Stack key={stat.id} direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                    <Iconify icon={stat.icon} width={18} sx={{ color: '#DF3E30' }} />
                    <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                      {stat.text}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            )}

            <Stack direction="row" spacing={0.5} sx={{ mt: 'auto', color: 'primary.main', alignItems: 'center' }}>
              <Typography variant="subtitle2" sx={{ color: 'inherit' }}>
                View course
              </Typography>
              <Iconify
                className="course-arrow"
                icon="eva:arrow-forward-outline"
                sx={{ width: 16, height: 16, transition: 'transform 0.25s ease' }}
              />
            </Stack>
          </CardContent>
        </Card>
      </MotionInView>
    </Grid>
  );
}

// ----------------------------------------------------------------------

export default function AllCourseWidget() {
  const dispatch = useDispatch();
  const { courses, error, isLoading } = useSelector((state) => state.course);

  useEffect(() => {
    dispatch(getAllCourses());
  }, [dispatch]);

  // Tolerate either a bare array or the `{ results: [...] }` wrapper.
  const list = useMemo(
    () => (Array.isArray(courses) ? courses : courses?.results ?? []),
    [courses]
  );

  if (!list.length && isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: { xs: 8, md: 12 } }}>
        <CircularProgress size={32} thickness={4} />
      </Box>
    );
  }

  if (!list.length) {
    return (
      <Card sx={{ py: { xs: 8, md: 12 }, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ color: 'text.secondary', mb: 1 }}>
          No courses available
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.disabled' }}>
          {error ? 'We could not load the course list right now. Please try again later.' : 'New courses will be published here soon.'}
        </Typography>
      </Card>
    );
  }

  return (
    <Grid container spacing={{ xs: 3, md: 4 }} sx={{ alignItems: 'stretch' }}>
      {list.map((course, index) => (
        <CourseCard key={course?.slug ?? index} course={course} index={index} />
      ))}
    </Grid>
  );
}
